import React, { useState, useEffect } from "react";
import { Dialog } from "@headlessui/react";
import { motion, AnimatePresence } from "framer-motion";
import { FaClipboardCheck, FaShieldAlt, FaArrowLeft, FaCheck, FaLock } from "react-icons/fa";

// Button Components
const PreviousButton = ({ onClick, disabled = false }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className="flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 shadow-sm flex-1"
  >
    <FaArrowLeft className="text-xs" />
    Previous
  </button>
);

const SubmitButton = ({ onClick, disabled = false, loading = false, isUpdate = false }) => (
  <button
    type="submit"
    onClick={onClick}
    disabled={disabled || loading}
    className="flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-cyan-600 rounded-lg hover:from-blue-700 hover:to-cyan-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-sm flex-1"
  >
    {loading ? (
      <>
        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
        {isUpdate ? "Updating..." : "Submitting..."}
      </>
    ) : (
      <>
        {isUpdate ? "Update Profile" : "Submit Registration"}
        <FaCheck className="text-xs" />
      </>
    )}
  </button>
);

const RegisterPage4 = ({
  formData = {},
  initialData = null,
  student_id = null,
  onSubmit,
  onPrevious,
  onDataChange,
  apiBaseUrl = `${process.env.REACT_APP_BACKEND_URL}/api`,
  currentUserId = null,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  
  // Add state for user role
  const [userRole, setUserRole] = useState("user");
  const [isAdmin, setIsAdmin] = useState(false);

  // Check user role on component mount
  useEffect(() => {
    const checkUserRole = () => {
      const userData = localStorage.getItem("user");

      if (!userData) {
        setUserRole("user");
        setIsAdmin(false);
        return;
      }

      try {
        const parsedData = JSON.parse(userData);
        const role = (parsedData.role || "user").toLowerCase();

        setUserRole(role);
        setIsAdmin(role === "admin");

      } catch (error) {
        console.error("Error parsing user data:", error);
        setUserRole("user");
        setIsAdmin(false);
      }
    };

    checkUserRole();
  }, []);

  useEffect(() => {
    if (initialData && onDataChange) {
      const autoFillData = {
        agreedToDisclaimer:
          initialData.personal_info?.agreed_to_disclaimer || false,
      };

      const fieldsToUpdate = {};
      Object.keys(autoFillData).forEach((key) => {
        if (formData[key] === undefined && autoFillData[key] !== undefined) {
          fieldsToUpdate[key] = autoFillData[key];
        }
      });

      if (Object.keys(fieldsToUpdate).length > 0) {
        onDataChange(fieldsToUpdate);
      }
    }
  }, [initialData]);

  // Modified handleCheckboxChange to prevent changes for non-admin users
  const handleCheckboxChange = () => {
    // If not admin, prevent changes
    if (!isAdmin) {
      return;
    }
    
    const newValue = !formData.agreedToDisclaimer;
    onDataChange({ agreedToDisclaimer: newValue });
  };

  const saveCurrentDataToLocalStorage = () => {
    if (typeof window === "undefined") return;

    try {
      const currentData = {
        agreedToDisclaimer: formData.agreedToDisclaimer,
      };
      localStorage.setItem("register_4", JSON.stringify(currentData));
    } catch (error) {
      console.error("Error saving to localStorage:", error);
    }
  };

  const getLocalStorageData = () => {
    if (typeof window === "undefined") return {};
    const data = {};
    Object.keys(localStorage)
      .filter((key) => key.startsWith("register_"))
      .forEach((key) => {
        try {
          const item = localStorage.getItem(key);
          if (item) data[key] = JSON.parse(item);
        } catch (error) {
          console.error(`Error parsing ${key}:`, error);
        }
      });
    return data;
  };

  // ✅ NEW: Combine data with register_1 taking priority
  const combineAllRegistrationData = (formData = {}) => {
  const localStorageData = getLocalStorageData();

  const mergeWithFormData = (data) => {
    if (!data) return {};
    return data.form_data ? { ...data, ...data.form_data } : data;
  };

  // Extract data from localStorage
  const page1Data = mergeWithFormData(localStorageData.register_1) || {};
  const page2Data = mergeWithFormData(localStorageData.register_2) || {};
  const page3Data = mergeWithFormData(localStorageData.register_3) || {};
  const page4Data = mergeWithFormData(localStorageData.register_4) || {};

  // Merge (page1 overrides, but we will fix firstName/lastName after)
  const combinedData = {
    ...page2Data,
    ...page3Data,
    ...page4Data,
    ...page1Data,
    agreedToDisclaimer: formData.agreedToDisclaimer ?? true,
  };

  // ❗ Forcefully override with correct data from Page 2
  combinedData.firstName = page2Data.firstName || combinedData.firstName || "";
  combinedData.lastName  = page2Data.lastName  || combinedData.lastName  || "";
  combinedData.email  = page2Data.email  || combinedData.email  || "";

  // Remove unnecessary nested keys
  delete combinedData.form_data;

  return combinedData;
};


  const updateStudentProfile = async (data) => {
    try {
      const response = await fetch(
        `${apiBaseUrl}/update_student/${student_id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            ...data,
            updatedBy: currentUserId || 1,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || `HTTP error! status: ${response.status}`
        );
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error("API Error:", error);
      throw error;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.agreedToDisclaimer) {
      setErrorMessage("Please agree to the terms and conditions to continue.");
      setShowErrorModal(true);
      return;
    }

    setIsSubmitting(true);

    try {
      // Step 1: Save current page data
      if (typeof window !== "undefined") {
        saveCurrentDataToLocalStorage();
        window.dispatchEvent(new Event("forceSaveAllSteps"));
      }

      // Step 2: Short delay to ensure localStorage updates
      await new Promise((resolve) => setTimeout(resolve, 100));

      // Step 3: Combine data with page 1 priority
      const allRegistrationData = combineAllRegistrationData(formData);
      console.log("📦 Final data being sent to API:", allRegistrationData);

      let result;
      if (student_id) {
        result = await updateStudentProfile(allRegistrationData);
        if (result.success) {
          setSuccessMessage("Profile updated successfully!");
          setShowSuccessModal(true);
        } else {
          throw new Error(result.message || "Update failed");
        }
      } else {
        if (onSubmit) {
          result = await onSubmit(allRegistrationData);
          if (result?.success !== false) {
            setSuccessMessage("Registration submitted successfully!");
            setShowSuccessModal(true);
          } else {
            throw new Error(result?.message || "Submission failed");
          }
        }
      }
    } catch (error) {
      console.error("❌ Submission error:", error);
      setErrorMessage(
        error.message ||
          `Failed to ${
            student_id ? "update profile" : "submit registration"
          }. Please try again.`
      );
      setShowErrorModal(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSuccessClose = () => {
    setShowSuccessModal(false);
  };

  // Admin Lock Indicator Component
  const AdminLockIndicator = ({ isAdmin }) => {
    if (isAdmin) return null;
    
    return (
      <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
        <div className="flex items-center text-yellow-700">
          <FaLock className="mr-2" />
          <span className="text-sm font-medium">
            View-only mode: Only administrators can edit these fields
          </span>
        </div>
      </div>
    );
  };

  return (
    <div className="w-full bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-cyan-600 p-3 sm:p-4 md:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="text-center sm:text-left">
              <h1 className="text-xl sm:text-2xl font-bold text-white">
                Confirmation & Submission
                {!isAdmin && (
                  <span className="ml-3 text-sm bg-yellow-500 text-white px-2 py-1 rounded">
                    View Only
                  </span>
                )}
              </h1>
            </div>
            <div className="bg-blue-500/20 rounded-lg px-3 py-2">
              
            </div>
          </div>
        </div>

        <div className="overflow-y-auto max-h-[70vh] p-3 sm:p-4 md:p-6 lg:p-8 space-y-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Add admin lock indicator */}
            <AdminLockIndicator isAdmin={isAdmin} />
            
            {/* Data Processing Agreement Section */}
            <div className="bg-gray-50 rounded-lg sm:rounded-xl p-4 sm:p-5 border border-gray-200">
              <div className="flex items-center mb-4">
                <div className="bg-blue-100 p-2 rounded-lg mr-3">
                  <FaShieldAlt className="text-indigo-600 text-lg" />
                </div>
                <h3 className="text-base sm:text-lg font-bold text-gray-800">
                  Data Processing Agreement
                </h3>
              </div>
              <div className="text-gray-700 leading-relaxed text-justify text-sm space-y-3">
                <p>
                  I agree to GCRD processing personal data contained in this
                  form or other data which GCRD has received from me or may
                  obtain from other people. I agree to the processing of such
                  data for any purposes connected with my studies by GCRD to be
                  shared with partner education institutions.
                </p>
                <p>
                  In addition, I agree to GCRD processing personal data
                  described as Special Category Data as defined under the
                  General Data Protection Regulation, such processing to be
                  undertaken for any purposes as indicated in the declaration
                  above.
                </p>
                <p>
                  I understand and accept this statement. I confirm that the
                  information I have provided on this application is true,
                  complete and accurate and I agree any misinformation may lead
                  to cancellation of my application from GCRD and its partner
                  educational institutions. I confirm that I have declared all
                  previous study and have listed my highest qualification.
                </p>
              </div>
            </div>

            {/* Agreement Checkbox */}
            <div className={`flex items-start space-x-3 bg-gray-50 rounded-lg p-4 border border-gray-200 ${!isAdmin ? 'opacity-70' : ''}`}>
              <div className="flex items-center h-5 mt-0.5">
                <input
                  type="checkbox"
                  id="agree"
                  checked={!!formData.agreedToDisclaimer}
                  onChange={handleCheckboxChange}
                  disabled={!isAdmin}
                  className={`w-4 h-4 text-indigo-600 border-gray-300 focus:ring-indigo-500 rounded ${!isAdmin ? 'cursor-not-allowed opacity-60' : ''}`}
                />
              </div>
              <label 
                htmlFor="agree" 
                className={`text-sm font-medium flex-1 ${!isAdmin ? 'text-gray-500 cursor-not-allowed' : 'text-gray-700 cursor-pointer'}`}
                onClick={() => !isAdmin && null}
              >
                I understand and accept the Data Processing Agreement above. I confirm that all information provided is true, complete, and accurate.
              </label>
            </div>

            {/* Navigation Buttons */}
            <div className="flex flex-col sm:flex-row justify-between gap-3 pt-4 border-t border-gray-200">
              <PreviousButton 
                onClick={onPrevious} 
                disabled={isSubmitting}
              />
              <SubmitButton 
                onClick={handleSubmit}
                disabled={!formData.agreedToDisclaimer}
                loading={isSubmitting}
                isUpdate={!!student_id}
              />
            </div>
          </form>
        </div>

        {/* Error Modal */}
        <AnimatePresence>
        {showErrorModal && (
          <Dialog
            open
            onClose={() => setShowErrorModal(false)}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50"
          >
            <Dialog.Panel
              as={motion.div}
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ type: "spring", stiffness: 300, damping: 20 }}
              className="bg-white rounded-xl shadow-2xl p-6 max-w-sm w-full"
            >
              <Dialog.Title className="text-lg font-semibold text-gray-800 mb-4">
                Action Required
              </Dialog.Title>
              <p className="text-gray-600 text-sm mb-6">{errorMessage}</p>
              <div className="flex justify-end">
                <button
                  onClick={() => setShowErrorModal(false)}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition text-sm font-medium"
                >
                  OK
                </button>
              </div>
            </Dialog.Panel>
          </Dialog>
        )}

        {/* Success Modal */}
        {showSuccessModal && (
          <Dialog
            open
            onClose={handleSuccessClose}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50"
          >
            <Dialog.Panel
              as={motion.div}
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ type: "spring", stiffness: 300, damping: 20 }}
              className="bg-white rounded-xl shadow-2xl p-6 max-w-sm w-full text-center"
            >
              <div className="flex justify-center mb-4">
                <FaClipboardCheck className="text-green-600 text-3xl" />
              </div>
              <Dialog.Title className="text-lg font-semibold text-gray-800 mb-2">
                Success
              </Dialog.Title>
              <p className="text-gray-600 text-sm mb-6">{successMessage}</p>
              <button
                onClick={handleSuccessClose}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition text-sm font-medium"
              >
                Continue
              </button>
            </Dialog.Panel>
          </Dialog>
        )}
      </AnimatePresence>
    </div>
  );
};

export default RegisterPage4;